import React, { useState, useEffect } from "react";
import { fixedIndices } from "components/Admin/Cashflows/configItemValues";

import { HarnessForm } from "..";
import { loanTypeList, fieldTypes, floatingIndexList, fixedIndexList } from "../../../../constants";
import {
  dayCountTypes,
  interestRateTypes,
  amortTypesShort
} from "../../Constants/DropDownLists";

const NewFinancingForm = props => {
  const { setFieldValue, values: formikValues, dealValues, dealId } = props;
  const { nfRateType, nfAmortizationType, nfRateIndex } = formikValues;
  const loanTypeListFiltered = loanTypeList.filter(lt => lt.isAssumptionType);

  const [rateIndexOptions, setRateIndexOptions] = useState([]);
  const [ioPeriodFieldHidden, setIoPeriodFieldHidden] = useState(true);
  const [amortRateFieldHidden, setAmortRateFieldHidden] = useState(true);
  const [spreadFieldHidden, setSpreadFieldHidden] = useState(true);
  const [nfRateFieldHidden, setNfRateFieldHidden] = useState(true);

  // This handles when a user enters a deal ID and we are using this to prepopulate the new financing form.
  useEffect(() => {
    if (dealId) {
      Object.entries(dealValues).map(dv => {
        if (dv[0].startsWith("nf")) {
          setFieldValue(dv[0], dv[1]);
        }
        if (dv[0].startsWith("ef")) {
          setFieldValue(dv[0], "");
        }
      });
    }
  }, [dealValues]);

  useEffect(() => {
    setFieldValue("nfRateIndex", "");
    setFieldValue("nfAmortizationRate", "");
    setFieldValue("nfSpread", "");
    setFieldValue("nfRate", "");
    setRateIndexOptions(nfRateType === "FIXED" ? fixedIndexList : floatingIndexList);
    setAmortRateFieldHidden(nfRateType === "FIXED");
    setSpreadFieldHidden(nfRateType === "FIXED");
  }, [nfRateType]);

  useEffect(() => {
    setFieldValue("nfIoPeriods", "");
    nfAmortizationType === "INTEREST_ONLY" ? setIoPeriodFieldHidden(true) : setIoPeriodFieldHidden(false);
  }, [nfAmortizationType]);

  useEffect(() => {
    setFieldValue("nfSpread", "");
    setFieldValue("nfRate", "");

    if (nfRateIndex === fixedIndices.Treasurys || nfRateIndex === fixedIndices.SemiBond_Swaps) {
      setSpreadFieldHidden(false);
      setNfRateFieldHidden(true);
    }

    if (nfRateIndex === fixedIndices.InternallyFixed) {
      setSpreadFieldHidden(true);
      setNfRateFieldHidden(false);
    }
  }, [nfRateIndex]);

  const underwritingAssumptionsSection = [
    {
      sectionTitle: "Underwriting Assumptions",
      fields: [
        {
          label: "Loan Type",
          fieldName: "nfLoanType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: loanTypeListFiltered,
          onChange: ({ value }) => {
            setFieldValue("nfLoanType", value);
          }
        },
        {
          label: "Rate Type",
          fieldName: "nfRateType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: interestRateTypes,
          onChange: ({ value }) => {
            setFieldValue("nfRateType", value);
          }
        },
        {
          label: "Rate Index",
          fieldName: "nfRateIndex",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: rateIndexOptions,
          onChange: ({ value }) => {
            setFieldValue("nfRateIndex", value);
          }
        },
        {
          label: "Spread",
          fieldName: "nfSpread",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: spreadFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("nfSpread", target.value);
          }
        },
        {
          label: "Rate",
          fieldName: "nfRate",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: nfRateFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("nfRate", target.value);
          }
        },
        {
          label: "NOI",
          fieldName: "nfNoi",
          fieldType: fieldTypes.MONEY_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfNoi", target.value);
          }
        },
        {
          label: "Cap Rate",
          fieldName: "nfCapRate",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfCapRate", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Min DSCR",
          fieldName: "nfMinDscr",
          fieldType: fieldTypes.DECIMAL_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfMinDscr", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Max LTV",
          fieldName: "nfMaxLtv",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfMaxLtv", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Min Debt Yield",
          fieldName: "nfMinDebtYield",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfMinDebtYield", target.value);
          },
          decimalScale: 2
        }
      ]
    }
  ];

  const cashflowAssumptionsSection = [
    {
      sectionTitle: "Cashflow Assumptions",
      fields: [
        {
          label: "Start Date",
          fieldName: "nfStartDate",
          fieldType: fieldTypes.DATE_PICKER,
          selectValue: "value",
          onChange: e => {
            setFieldValue("nfStartDate", e?._d);
          }
        },
        {
          label: "Loan Term (m)",
          fieldName: "nfLoanTerm",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfLoanTerm", target.value);
          }
        },
        {
          label: "Daycount",
          fieldName: "nfDaycount",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          options: dayCountTypes,
          shortWidth: true,
          onChange: ({ value }) => {
            setFieldValue("nfDaycount", value);
          }
        },
        {
          label: "Closing Costs (%)",
          fieldName: "nfClosingCosts",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfClosingCosts", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Amortization Type",
          fieldName: "nfAmortizationType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: amortTypesShort,
          onChange: ({ value }) => {
            setFieldValue("nfAmortizationType", value);
          }
        },
        {
          label: "Amortization Term (m)",
          fieldName: "nfAmortizationTerm",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("nfAmortizationTerm", target.value);
          }
        },
        {
          label: "I/O Periods",
          fieldName: "nfIoPeriods",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          hidden: ioPeriodFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("nfIoPeriods", target.value);
          }
        },
        {
          label: "Amortization Rate",
          fieldName: "nfAmortizationRate",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: amortRateFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("nfAmortizationRate", target.value);
          },
          decimalScale: 2
        }
      ]
    }
  ];

  const sections = [...underwritingAssumptionsSection, ...cashflowAssumptionsSection];

  return <HarnessForm title="New Financing" sections={sections} />;
};

export default NewFinancingForm;
